
import { computed, defineComponent, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import SubscriptionOverview from '@/modules/marketplace/components/billing/SubscriptionOverview.vue'
import DetachChannelModal from '@/modules/marketplace/components/billing/DetachChannelModal.vue'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import SpinArrows from '@/assets/vuetifyIcons/spinArrows.vue'
import { useProject } from '@/hooks/useProject'
import { useBillingStore } from '@/store/stores/billing'
import { useConfirmStore } from '@/store/stores/confirm'
import { cancelScheduleTariffChange, getSubscriptions, paymentConfirmClick, toggleSubscription, updatePaymentPayloadStatus } from '@/api/marketplace'
import { applyCancelSubscriptionQueryParams } from '@/helpers/channels/getConnectorChannelDisplayName'
import { resolveSubscriptionPeriod } from '@/modules/marketplace/subscriptionPeriodDiscount'
import { formatSpecialPaymentLineName } from '@/helpers/billing/specialPayment'
import { getPendingSlots, getSlots, unchainSlot } from '@/api/networkFixer'
import { openChannelCreationSidebar } from '@/helpers/channels/openChannelCreationSidebar'
import config from '@root/config'
import { BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS } from '@/helpers/beautyAutopilot'
import type {
    SubscriptionsScreenDto,
    SubscriptionChannelKind,
    SubscriptionLineRowDto,
} from '@/modules/marketplace/data/subscriptionsScreen.types'

interface BackendSubscriptionRow {
    kind: 'tariff' | 'slot' | 'module'
    name: string | null
    slotId?: string
    inTariff?: boolean
    totalRub: number
    subscriptionId: number
    paymentPayloadId?: number
    pricePerMonthRub: number
    integrationId?: string
}

interface ChannelSlotPayload {
    id: string
    channelConnectorId?: string | null
    type?: string | null
    isTariff?: boolean
    subscriptionId?: number | null
    renewalMode?: string | null
    channelDeletedDate?: string | null
}

interface BackendSubscriptionBlock {
    rows: BackendSubscriptionRow[]
    periodTo: string
    periodFrom: string
    paymentPayloadId?: number
    isAutoRenewalEnabled?: boolean
    isTrial?: boolean
    periodMonths?: number | null
    totalPeriodRub?: number
    totalMonthlyRub?: number
    renewalPriceRub?: number | null
    scheduledChanges?: {
        activeFrom: string
        periodMonths: string
        paymentStatus?: 'created' | 'pending' | 'paid' | 'failed'
        integrationName: string
        paymentPayloadId?: number | string
        paymentUrl?: string | null
    } | null
    paymentMethod?: {
        provider?: 'yookassa' | 'invoice' | null
        last4?: string | number | null
    } | null
}

interface BackendSubscriptionsResponse {
    multiSubscription: BackendSubscriptionBlock | null
    addonSubscriptions: BackendSubscriptionBlock[]
}

interface BackendSubscriptionsEnvelope {
    success?: boolean
    data?: BackendSubscriptionsResponse | null
}

type UiSubscriptionChannelKind = 'tariff' | 'whatsapp' | 'telegram' | 'max' | 'sms'
const SMS_CHANNEL_SLOT_TYPE = 'smsChannelSlot'
type AddonPaymentStatusTone = 'warning' | 'danger' | 'processing'
type AddonPaymentStatus = { label: string; tone: AddonPaymentStatusTone } | null
type UiAddonSubscription = SubscriptionsScreenDto['addonSubscriptions'][number] & {
    pendingPayment?: boolean
    renewalMode?: string
    paymentPayloadId?: number
}
type UiSubscriptionLineRow = SubscriptionLineRowDto & {
    pendingPayment?: boolean
    paymentPayloadId?: number
    slotId?: string
    inTariff?: boolean
    kind?: 'tariff' | 'slot' | 'module'
    slotType?: string | null
    integrationId?: string
}
interface PendingSlotLineItem {
    name?: string | null
    amount?: number
    periodTo?: string
    periodFrom?: string
    inTariff?: boolean
    integrationId?: string
}
interface PendingSlotPayload {
    id: number
    type?: string | null
    kind?: string | null
    integration_id?: string | null
    renewal_mode?: string | null
    subscription_id?: number | null
    from_subscription?: boolean | null
    price?: {
        lineItems?: PendingSlotLineItem[]
    }
}

const MAX_SUBSCRIPTION_PERIOD_MONTHS = 24
const DOCTOR_BOT_TARIFF_ID = 'tariff1'
const SMART_BOT_TARIFF_ID = 'tariff2'

export default defineComponent({
    name: 'SubscriptionsTab',
    components: { SubscriptionOverview, DetachChannelModal, SpinnerLoader, SpinArrows },
    setup() {
        const { project } = useProject()
        const billingStore = useBillingStore()
        const confirmStore = useConfirmStore()
        const root = getCurrentInstance()?.proxy
        const loading = ref(true)
        const subscriptionsRefreshing = ref(false)
        const cancellingPendingPaymentId = ref<number | null>(null)
        const loadError = ref<string | null>(null)
        const screen = ref<SubscriptionsScreenDto | null>(null)
        const channelSlots = ref<ChannelSlotPayload[]>([])
        const highlightedPaymentPayloadId = ref<number | null>(null)
        const paymentHighlightRowRef = ref<HTMLElement | null>(null)
        const paymentHighlightScrollScheduled = ref(false)
        const currentPeriodInMonths = ref(MAX_SUBSCRIPTION_PERIOD_MONTHS)
        const currentTariffId = ref<string | null>(null)
        const isCurrentTariffTrial = ref<boolean>(false)
        const legacyLayoutEnabled = false
        const promoBlocks = computed<SubscriptionsScreenDto['promoBlocks']>(() => {
            const blocks: (SubscriptionsScreenDto['promoBlocks'][number] & { show: boolean })[] = [
                {
                    id: 'long_period',
                    emoji: '%',
                    title: 'Хотите платить меньше?',
                    description: 'Экономьте до 25% при оплате длительного периода',
                    ctaLabel: 'Хочу скидку',
                    actionType: 'claim_discount',
                    show: currentPeriodInMonths.value < MAX_SUBSCRIPTION_PERIOD_MONTHS,
                },
                {
                    id: 'more_channels',
                    emoji: '+',
                    title: 'Хотите больше каналов?',
                    description: 'Добавьте канал: WhatsApp, Telegram или MAX за 990 ₽ / мес.',
                    ctaLabel: 'Купить канал',
                    actionType: 'claim_channel',
                    show: true,
                },
                {
                    id: 'promo_free_channels',
                    emoji: '🚀',
                    title: 'Больше возможностей',
                    description: '3 канала + аналитика + KPI',
                    ctaLabel: 'Повысить тариф',
                    actionType: 'go_tariff',
                    show: currentTariffId.value === DOCTOR_BOT_TARIFF_ID && !isCurrentTariffTrial.value,
                },
            ]

            return blocks.filter(block => block.show).map(({ show, ...block }) => block)
        })
        const getUpgradeSubscriptionPeriod = () => {
            if (currentPeriodInMonths.value === 12 || currentPeriodInMonths.value === 24) {
                return String(currentPeriodInMonths.value)
            }
            return '6'
        }
        const resolveSmartBotTariffId = () => {
            const match = (billingStore.tariffs || []).find(
                tariff =>
                    tariff.id === SMART_BOT_TARIFF_ID ||
                    tariff.baseTariffId === SMART_BOT_TARIFF_ID
            )
            return match?.id || SMART_BOT_TARIFF_ID
        }
        const openInitialTariffPurchase = async (projectId: string) => {
            if (!billingStore.tariffs.length) {
                await billingStore.fetchTariffs(projectId)
            }
            billingStore.setAddChannelContext(null)
            const previous = billingStore.getPreviousTariff(projectId)
            const matched = previous?.integrationId
                ? (billingStore.tariffs || []).find(
                    tariff =>
                        tariff.id === previous.integrationId ||
                        tariff.baseTariffId === previous.integrationId
                )
                : null
            if (!matched) {
                billingStore.selectedTariffId = resolveSmartBotTariffId()
                billingStore.selectedSubscriptionPeriod = '6'
                billingStore.setPaymentModalOpened(true, false)
                return
            }
            billingStore.selectedTariffId = matched.id
            billingStore.selectedSubscriptionPeriod = resolveSubscriptionPeriod(
                previous?.periodInMonths != null ? String(previous.periodInMonths) : null,
                matched,
                { preferredFallback: '6' }
            )
            billingStore.setPaymentModalOpened(true, false)
        }
        const getTariffLogicalId = (
            tariff?: { id?: string; baseTariffId?: string | null } | null,
            fallback = ''
        ) => String(tariff?.baseTariffId || tariff?.id || fallback)

        const nextUpgradeTariffId = computed(() => {
            const currentId = String(currentTariffId.value || '')
            if (!currentId) return null
            const currentFromStore = billingStore.getTariffById(currentId)
            const logical = getTariffLogicalId(currentFromStore, currentId)
            if (logical !== DOCTOR_BOT_TARIFF_ID) return null
            const upgrade = (billingStore.tariffs || []).find(
                tariff => getTariffLogicalId(tariff) === SMART_BOT_TARIFF_ID
            )
            return upgrade?.id || SMART_BOT_TARIFF_ID
        })
        const canUpgradeSubscription = computed(() => Boolean(nextUpgradeTariffId.value && !isCurrentTariffTrial.value))

        const handleUpgradeSubscription = async () => {
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (!billingStore.tariffs.length) {
                await billingStore.fetchTariffs(projectId)
            }
            const targetTariffId = nextUpgradeTariffId.value
            if (!targetTariffId) {
                root?.$store?.dispatch('callNotify', 'Для текущего тарифа нет доступного улучшения')
                return
            }
            billingStore.setAddChannelContext(null)
            billingStore.selectedTariffId = targetTariffId
            billingStore.selectedSubscriptionPeriod = getUpgradeSubscriptionPeriod()
            billingStore.setPaymentModalOpened(true, false, true, true)
        }

        const formatRub = (rub: number) => billingStore.formatPrice(rub)
        const formatMonthly = (rub: number) => `${billingStore.formatPrice(rub)} /мес.`

        const channelClass = (kind: UiSubscriptionChannelKind) => {
            const map: Record<UiSubscriptionChannelKind, string> = {
                tariff: 'subscription-card__channel--tariff',
                whatsapp: 'subscription-card__channel--wa',
                telegram: 'subscription-card__channel--tg',
                max: 'subscription-card__channel--max',
                sms: 'subscription-card__channel--sms',
            }
            return map[kind] || map.tariff
        }

        const channelIconSrc = (kind: UiSubscriptionChannelKind) => {
            const map: Record<UiSubscriptionChannelKind, string> = {
                tariff: require('@/assets/icons/tariff-briefcase.svg'),
                whatsapp: require('@/assets/icons/whatsApp-white.svg'),
                telegram: require('@/assets/icons/telegram-white.svg'),
                max: require('@/assets/icons/max-white.svg'),
                sms: require('@/assets/img/messenger-logo/sms-logo.png'),
            }
            return map[kind] || map.tariff
        }

        const channelKindFromName = (name: string | null): UiSubscriptionChannelKind => {
            if (!name) return 'tariff'
            const normalized = String(name).toLowerCase().replace(/[\s_-]+/g, '')
            if (normalized.includes('sms')) return 'sms'
            if (normalized.includes('telegram')) return 'telegram'
            if (normalized.includes('max')) return 'max'
            if (normalized.includes('whatsapp')) return 'whatsapp'
            return 'tariff'
        }

        const roleLabelFromRow = (row: BackendSubscriptionRow): string => {
            if (row.kind === 'tariff') return 'Тариф'
            if (row.kind === 'module') return 'Модуль'
            return row.inTariff ? 'Входит в тариф' : 'Доп. канал'
        }

        const shortenSubscriptionDisplayName = (name?: string | null) => {
            const raw = String(name || '').trim()
            if (raw.toLowerCase() === 'дополнительные каскады') return 'Доп. каскады'
            return raw
        }

        const titleFromRow = (row: BackendSubscriptionRow): string => {
            if (row.kind === 'tariff') return row.name || 'Тариф'
            if (row.kind === 'module') return shortenSubscriptionDisplayName(row.name) || 'Модуль'
            return formatSpecialPaymentLineName(row.name, 'slot')
        }

        const getMonthsByPeriodRange = (periodFrom: string, periodTo: string): string | null => {
            const from = new Date(periodFrom)
            const to = new Date(periodTo)
            if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null
            const months =
                (to.getFullYear() - from.getFullYear()) * 12 +
                (to.getMonth() - from.getMonth())
            return months > 0 ? String(months) : null
        }
        const parsePeriodToDate = (periodTo: string): Date | null => {
            if (!periodTo) return null
            const raw = String(periodTo).trim()

            // Backend часто отдает YYYY-MM-DD; парсим явно в локальную дату.
            const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
            if (ymdMatch) {
                const year = Number(ymdMatch[1])
                const month = Number(ymdMatch[2]) - 1
                const day = Number(ymdMatch[3])
                const date = new Date(year, month, day)
                if (!Number.isNaN(date.getTime())) return date
                return null
            }

            const fallback = new Date(raw)
            if (Number.isNaN(fallback.getTime())) return null
            return fallback
        }

        const formatDateDdMmYy = (date: Date): string => {
            const d = String(date.getDate()).padStart(2, '0')
            const m = String(date.getMonth() + 1).padStart(2, '0')
            const y = String(date.getFullYear()).slice(-2)
            return `${d}.${m}.${y}`
        }

        const formatAutoRenewalNextPaymentLine = (periodTo: string): string => {
            const date = parsePeriodToDate(periodTo || '')
            return date ? formatDateDdMmYy(date) : ''
        }

        const formatScheduledChangeDate = (activeFrom: string): string => {
            const date = parsePeriodToDate(activeFrom || '')
            if (!date) return ''
            return date
                .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
                .replace(/\s?г\.$/, '')
        }

        const monthWordRu = (n: number): string => {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return 'месяц'
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'месяца'
            return 'месяцев'
        }

        const formatScheduledChangePeriod = (periodMonths: string): string => {
            const months = Number(periodMonths)
            if (!Number.isFinite(months) || months <= 0) return ''
            return `(${months} ${monthWordRu(months)})`
        }

        const daysWordRu = (n: number): string => {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return 'день'
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
            return 'дней'
        }

        /** «остался 1 день» / «осталось 2 дня» / «осталось 11 дней» и т.д. */
        const formatRemainingDaysWithVerbRu = (diffDays: number): string => {
            const mod10 = diffDays % 10
            const mod100 = diffDays % 100
            const verb = mod10 === 1 && mod100 !== 11 ? 'остался' : 'осталось'
            return `${verb} ${diffDays} ${daysWordRu(diffDays)}`
        }

        /** Дата окончания периода и сколько календарных дней осталось до неё, напр. «10.04.26 (осталось 78 дней)», «10.04.26 (остался 1 день)». */
        const formatMultiSubscriptionPeriodUntilLine = (periodTo: string): string => {
            const endDate = parsePeriodToDate(periodTo)
            if (!endDate) return ''

            const today = new Date()
            const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
            const startOfEndDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
            const msInDay = 24 * 60 * 60 * 1000
            const diffDays = Math.floor((startOfEndDate.getTime() - startOfToday.getTime()) / msInDay)

            const dateStr = formatDateDdMmYy(endDate)
            if (diffDays < 0) return `${dateStr} (период истёк)`
            return `${dateStr} (${formatRemainingDaysWithVerbRu(diffDays)})`
        }

        const getPaymentDeadlineStatus = (periodTo: string): AddonPaymentStatus => {
            const endDate = parsePeriodToDate(periodTo || '')
            if (!endDate) return null

            const today = new Date()
            const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
            const startOfEndDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
            const msInDay = 24 * 60 * 60 * 1000
            const diffDays = Math.floor((startOfEndDate.getTime() - startOfToday.getTime()) / msInDay)

            if (diffDays >= 5) return null
            if (diffDays >= 1 && diffDays <= 4) return { label: 'Не забудьте оплатить', tone: 'warning' }
            return { label: 'Требуется оплата', tone: 'danger' }
        }

        const getAddonPaymentStatus = (addon: UiAddonSubscription): AddonPaymentStatus => {
            if (addon?.pendingPayment) return { label: 'Оплата в обработке', tone: 'processing' }
            return getPaymentDeadlineStatus(addon?.periodTo || '')
        }

        const mapRows = (rows: BackendSubscriptionRow[]): UiSubscriptionLineRow[] =>
            rows.map(row => {
                const slot =
                    (row.slotId
                        ? channelSlots.value.find(item => String(item.id) === String(row.slotId))
                        : undefined) ||
                    (typeof row.subscriptionId === 'number'
                        ? channelSlots.value.find(
                              item => Number(item.subscriptionId) === Number(row.subscriptionId)
                          )
                        : undefined)
                const slotType = slot?.type || null

                return {
                    channelKind: (row.kind === 'tariff' || row.kind === 'module'
                        ? 'tariff'
                        : channelKindFromName(row.name)) as SubscriptionChannelKind,
                    name: titleFromRow(row),
                    roleLabel: roleLabelFromRow(row),
                    pricePerMonthRub: row.pricePerMonthRub,
                    totalRub: row.totalRub,
                    subscriptionId: row.subscriptionId,
                    paymentPayloadId: row.paymentPayloadId,
                    slotId: row.slotId,
                    inTariff: row.inTariff === true,
                    kind: row.kind,
                    slotType,
                    integrationId: row.integrationId,
                }
            })

        const aggregateAddonRow = (addon: UiAddonSubscription): UiSubscriptionLineRow => {
            const rows = (addon.rows || []) as UiSubscriptionLineRow[]
            const first = rows[0]
            if (!first) {
                return {
                    channelKind: 'tariff',
                    name: addon.title,
                    roleLabel: '',
                    pricePerMonthRub: 0,
                    totalRub: 0,
                    pendingPayment: addon.pendingPayment,
                }
            }
            return {
                channelKind: first.channelKind,
                name: first.name,
                roleLabel: first.roleLabel,
                pricePerMonthRub: rows.reduce((sum, r) => sum + Number(r.pricePerMonthRub || 0), 0),
                totalRub: rows.reduce((sum, r) => sum + Number(r.totalRub || 0), 0),
                subscriptionId: first.subscriptionId,
                slotId: first.slotId,
                inTariff: first.inTariff,
                kind: first.kind,
                slotType: first.slotType,
                integrationId: first.integrationId,
                pendingPayment: addon.pendingPayment || first.pendingPayment,
            }
        }

        const addonSubscriptionsTableRows = computed(() =>
            ((screen.value?.addonSubscriptions || []) as UiAddonSubscription[]).map(addon => ({
                id: addon.id,
                addon,
                row: aggregateAddonRow(addon),
                paymentPayloadId: addon.paymentPayloadId,
                paymentStatus: getAddonPaymentStatus(addon),
            }))
        )

        const multiSubscriptionTableRows = computed(() => {
            const multiPaymentPayloadId = Number(
                (screen.value?.multiSubscription as { paymentPayloadId?: number } | null)?.paymentPayloadId
            )
            const fallbackPaymentPayloadId =
                Number.isFinite(multiPaymentPayloadId) && multiPaymentPayloadId > 0
                    ? multiPaymentPayloadId
                    : undefined

            return (screen.value?.multiSubscription?.rows || []).map((row, idx) => ({
                id: `multi-row-${idx}`,
                row: row as UiSubscriptionLineRow,
                paymentPayloadId:
                    (row as UiSubscriptionLineRow).paymentPayloadId ?? fallbackPaymentPayloadId,
                paymentStatus: (row as UiSubscriptionLineRow).pendingPayment
                    ? { label: 'Оплата в обработке', tone: 'processing' as const }
                    : null,
            }))
        })

        const parsePaymentHighlightFromRoute = () => {
            const route = root?.$route
            if (!route) {
                highlightedPaymentPayloadId.value = null
                return
            }

            const status = String(route.query.status || '').toLowerCase()
            const idRaw = Array.isArray(route.query.id) ? route.query.id[0] : route.query.id
            if (status !== 'success' || idRaw == null || idRaw === '') {
                highlightedPaymentPayloadId.value = null
                return
            }

            const paymentPayloadId = Number(idRaw)
            highlightedPaymentPayloadId.value =
                Number.isFinite(paymentPayloadId) && paymentPayloadId > 0 ? paymentPayloadId : null
        }

        const paymentHighlightTargetId = computed(() => {
            const targetId = highlightedPaymentPayloadId.value
            if (targetId == null) return null

            const hasMatchingRow = [...multiSubscriptionTableRows.value, ...addonSubscriptionsTableRows.value].some(
                item => item.paymentPayloadId != null && Number(item.paymentPayloadId) === targetId
            )

            return hasMatchingRow ? targetId : null
        })

        const isPaymentHighlighted = (paymentPayloadId?: number | null) =>
            paymentHighlightTargetId.value != null &&
            paymentPayloadId != null &&
            Number(paymentPayloadId) === paymentHighlightTargetId.value

        const setPaymentHighlightRowRef = (
            el: Element | ComponentPublicInstance | null,
            paymentPayloadId?: number | null
        ) => {
            if (!el || !isPaymentHighlighted(paymentPayloadId)) return
            const node = '$el' in el ? el.$el : el
            if (node instanceof HTMLElement && !paymentHighlightRowRef.value) {
                paymentHighlightRowRef.value = node
            }
        }

        const scrollToPaymentHighlight = async () => {
            if (!paymentHighlightTargetId.value || paymentHighlightScrollScheduled.value) return

            paymentHighlightScrollScheduled.value = true
            await nextTick()
            paymentHighlightRowRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        const maxBusinessSlotIntegrationId =
            config.marketplaceModulesIds[config.buildType].maxBusiness
        const moduleIds = config.marketplaceModulesIds[config.buildType]
        const smsChannelSlotIntegrationId = moduleIds.sms
        const channelLikeModuleIds = new Set([
            String(moduleIds.dhChat),
            String(moduleIds.customCascades),
            String(moduleIds.zvonoBot),
            String(moduleIds.businessPulseReport),
            String(moduleIds.opz2),
            String(moduleIds.opz2KPISettings),
            String(moduleIds.zvonoBotSettings),
            String(moduleIds.hotWindows),
            ...Object.values(BEAUTY_AUTOPILOT_PLAN_INTEGRATION_IDS),
        ])
        const isChannelLikeModuleRow = (row?: UiSubscriptionLineRow | null) => {
            if (!row) return false
            const integrationId = String(row.integrationId || '')
            if (integrationId && channelLikeModuleIds.has(integrationId)) return true
            const name = String(row.name || '').toLowerCase()
            return name.includes('мультичат') || name.includes('каскад') || name.includes('отчёт 2.0') || name.includes('опз 2.0') || name.includes('горячие окна') || name.includes('аудиорассылки') || name.includes('звонобот')
        }
        const isSmsChannelSlotType = (value?: string | null) =>
            String(value || '') === SMS_CHANNEL_SLOT_TYPE ||
            String(value || '') === String(smsChannelSlotIntegrationId)
        const resolvePendingSlotType = (slot: PendingSlotPayload): string | null => {
            if (slot?.type === 'maxSlot') return 'maxSlot'
            if (slot?.integration_id === maxBusinessSlotIntegrationId) return 'maxSlot'
            const lineItem = slot?.price?.lineItems?.[0]
            if (lineItem?.integrationId === maxBusinessSlotIntegrationId) return 'maxSlot'
            if (isSmsChannelSlotType(slot?.type) || isSmsChannelSlotType(slot?.integration_id)) {
                return SMS_CHANNEL_SLOT_TYPE
            }
            if (isSmsChannelSlotType(lineItem?.integrationId)) return SMS_CHANNEL_SLOT_TYPE
            return slot?.type || null
        }

        const getSlotById = (slotId?: string) =>
            channelSlots.value.find(slot => String(slot.id) === String(slotId))

        const resolveSlotForRow = (row: UiSubscriptionLineRow) => {
            if (row.slotId) {
                const slotById = getSlotById(row.slotId)
                if (slotById) return slotById
            }
            if (typeof row.subscriptionId === 'number') {
                return channelSlots.value.find(
                    slot => Number(slot.subscriptionId) === Number(row.subscriptionId)
                )
            }
            return undefined
        }

        const isUnoccupiedSlotRow = (row: UiSubscriptionLineRow) => {
            if (row.pendingPayment || row.kind === 'tariff' || row.kind === 'module') return false
            const slot = resolveSlotForRow(row)
            if (!slot) return false
            const isSlotLike =
                !row.kind ||
                row.kind === 'slot' ||
                isSmsChannelSlotType(row.kind) ||
                isSmsChannelSlotType(row.slotType) ||
                isSmsChannelSlotType(slot.type)
            if (!isSlotLike) return false
            return !slot.channelConnectorId
        }

        const isMaxBusinessSlotRow = (row: UiSubscriptionLineRow) => {
            if (row.kind === 'tariff') return false
            if (row.slotType === 'maxSlot') return true
            const slot = resolveSlotForRow(row)
            return slot?.type === 'maxSlot'
        }

        const openSlotConnect = (row: UiSubscriptionLineRow) => {
            const slot = resolveSlotForRow(row)
            if (!slot?.id || !root) return
            const opened = openChannelCreationSidebar(
                root,
                {
                    isActive: false,
                    _slotId: slot.id,
                },
                'new',
            )
            if (!opened) return
            if (slot?.type === 'maxSlot' || row.slotType === 'maxSlot') {
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.set('group', 'maxBusiness')
                window.history.replaceState({}, '', newUrl.toString())
            }
        }

        const mapPendingSlotDisplayName = (rawName: string) => {
            const normalized = rawName.trim().toLowerCase()
            if (normalized === 'слот канала' || normalized === 'новые каналы') return 'Свободный канал'
            return formatSpecialPaymentLineName(rawName, 'slot')
        }

        const mapPendingSlotLineRow = (slot: PendingSlotPayload, idx: number): UiSubscriptionLineRow => {
            const lineItem = slot?.price?.lineItems?.[0]
            const amount = Number(lineItem?.amount || 0)
            const isModule = slot?.type === 'module' || slot?.kind === 'module'
            const slotType = resolvePendingSlotType(slot)
            const rawName = String(lineItem?.name || '')
            const mappedName = isModule
                ? (shortenSubscriptionDisplayName(rawName) || 'Модуль')
                : mapPendingSlotDisplayName(rawName)
            const inTariff = lineItem?.inTariff === true

            return {
                channelKind: (isModule ? 'tariff' : channelKindFromName(rawName || mappedName)) as SubscriptionChannelKind,
                name: mappedName,
                roleLabel: isModule ? 'Модуль' : (inTariff ? 'Входит в тариф' : 'Доп. канал'),
                pricePerMonthRub: amount,
                totalRub: amount,
                pendingPayment: true,
                paymentPayloadId: slot.id,
                inTariff,
                kind: isModule ? 'module' : 'slot',
                slotType,
                integrationId: String(lineItem?.integrationId || slot?.integration_id || ''),
            }
        }

        const mapPendingSlotToAddon = (slot: PendingSlotPayload, idx: number): UiAddonSubscription => {
            const lineItem = slot?.price?.lineItems?.[0]
            const periodFrom = lineItem?.periodFrom || ''
            const periodTo = lineItem?.periodTo || ''

            return {
                id: `pending-addon-${slot?.id || idx}`,
                title: 'Дополнительные модули',
                subscriptionId: 0,
                periodFrom,
                periodTo,
                periodDescription: formatMultiSubscriptionPeriodUntilLine(periodTo),
                isAutoRenewalEnabled: false,
                pendingPayment: true,
                paymentPayloadId: slot.id,
                renewalMode: slot.renewal_mode || 'standalone',
                rows: [mapPendingSlotLineRow(slot, idx)],
            }
        }

        const splitPendingSlots = (
            pendingSlots: PendingSlotPayload[],
            existingSubscriptionIds: Set<number> = new Set()
        ) => {
            const tariffRows: UiSubscriptionLineRow[] = []
            const standaloneAddons: UiAddonSubscription[] = []
            const renewalPayloadBySubscriptionId = new Map<number, number>()

            ;(pendingSlots || []).forEach((slot, idx) => {
                if (slot?.renewal_mode === 'with_tariff') {
                    tariffRows.push(mapPendingSlotLineRow(slot, idx))
                    return
                }
                const subscriptionId = Number(slot?.subscription_id)

                if (slot?.from_subscription && subscriptionId && existingSubscriptionIds.has(subscriptionId)) {
                    renewalPayloadBySubscriptionId.set(subscriptionId, Number(slot.id))
                    return
                }
                standaloneAddons.push(mapPendingSlotToAddon(slot, idx))
            })

            return { tariffRows, standaloneAddons, renewalPayloadBySubscriptionId }
        }

        const mapPaymentMethod = (
            paymentMethod?: BackendSubscriptionBlock['paymentMethod'],
        ): NonNullable<SubscriptionsScreenDto['multiSubscription']>['paymentMethod'] => {
            if (!paymentMethod) return null
            const provider =
                paymentMethod.provider === 'yookassa' || paymentMethod.provider === 'invoice'
                    ? paymentMethod.provider
                    : null
            const last4Raw = paymentMethod.last4
            const last4 = last4Raw == null || last4Raw === '' ? null : String(last4Raw)
            return { provider, last4 }
        }

        const mapResponseToScreen = (payload: BackendSubscriptionsResponse): SubscriptionsScreenDto => {
            const multiRows = payload.multiSubscription?.rows || []
            const multiSubscription = payload.multiSubscription
                ? {
                      id: String(payload.multiSubscription.rows.find(row => row.kind === 'tariff')?.subscriptionId || 'multi'),
                      title: 'Подписка',
                      subscriptionId:
                          payload.multiSubscription.rows.find(row => row.kind === 'tariff')?.subscriptionId ||
                          payload.multiSubscription.rows[0]?.subscriptionId ||
                          0,
                      periodFrom: payload.multiSubscription.periodFrom,
                      periodTo: payload.multiSubscription.periodTo,
                      periodDescription: formatMultiSubscriptionPeriodUntilLine(
                          payload.multiSubscription.periodTo
                      ),
                      periodMonths: payload.multiSubscription.periodMonths ?? null,
                      isAutoRenewalEnabled: payload.multiSubscription.isAutoRenewalEnabled === true,
                      scheduledChanges: payload.multiSubscription.scheduledChanges || null,
                      paymentPayloadId: payload.multiSubscription.paymentPayloadId,
                      rows: mapRows(multiRows).map(row => ({
                          ...row,
                          paymentPayloadId: row.paymentPayloadId ?? payload.multiSubscription?.paymentPayloadId,
                      })),
                      totalMonthlyRub: payload.multiSubscription.totalMonthlyRub || 0,
                      totalPeriodRub: payload.multiSubscription.totalPeriodRub || 0,
                      renewalPriceRub: payload.multiSubscription.renewalPriceRub ?? null,
                      paymentMethod: mapPaymentMethod(payload.multiSubscription.paymentMethod),
                  }
                : null

            const addonSubscriptions: UiAddonSubscription[] = (payload.addonSubscriptions || []).map((addon, idx) => ({
                id: String(addon.rows[0]?.subscriptionId || `addon-${idx}`),
                title: 'Дополнительные модули',
                subscriptionId: addon.rows[0]?.subscriptionId || 0,
                periodFrom: addon.periodFrom,
                periodTo: addon.periodTo,
                periodDescription: formatMultiSubscriptionPeriodUntilLine(addon.periodTo),
                isAutoRenewalEnabled: addon.isAutoRenewalEnabled === true,
                isTrial: addon.isTrial === true,
                paymentPayloadId: addon.paymentPayloadId,
                rows: mapRows(addon.rows || []).map(row => ({
                    ...row,
                    paymentPayloadId: row.paymentPayloadId ?? addon.paymentPayloadId,
                })),
            }))

            return {
                // sectionTitle: 'Счета',
                multiSubscription,
                promoBlocks: promoBlocks.value,
                addonSubscriptions,
            }
        }

        const load = async ({ silent = false }: { silent?: boolean } = {}) => {
            const projectId = project.value?.id
            if (!projectId) {
                loadError.value = 'Проект не выбран'
                loading.value = false
                return
            }
            if (!silent) {
                loading.value = true
            }
            loadError.value = null
            const [{ data, error }, { data: pendingData, error: pendingError }, { data: slotsData, error: slotsError }] =
                await Promise.all([
                getSubscriptions(projectId),
                getPendingSlots({ projectId }),
                getSlots({ projectId }),
            ])
            if (!silent) {
                loading.value = false
            }
            if (error) {
                loadError.value = error
                screen.value = null
                return
            }
            if (pendingError) {
                root?.$store?.dispatch('callNotify', 'Не удалось получить подписки в обработке')
            }
            if (slotsError) {
                channelSlots.value = []
            } else {
                channelSlots.value = Array.isArray(slotsData) ? slotsData : []
            }
            const payload = data as BackendSubscriptionsResponse | BackendSubscriptionsEnvelope | null
            const resolvedPayload =
                payload && 'data' in payload && payload.data
                    ? payload.data
                    : (payload as BackendSubscriptionsResponse | null)
            const pendingPayloadRaw = pendingData as PendingSlotPayload[] | { data?: PendingSlotPayload[] } | null
            const pendingSlots = Array.isArray(pendingPayloadRaw)
                ? pendingPayloadRaw
                : Array.isArray(pendingPayloadRaw?.data)
                    ? pendingPayloadRaw.data
                    : []
            let currentTariff = billingStore.getCurrentTariff(projectId)
            if (!currentTariff?.integrationId) {
                await billingStore.fetchCurrentTariff(projectId)
                currentTariff = billingStore.getCurrentTariff(projectId)
            }
            currentPeriodInMonths.value = Number(currentTariff?.periodInMonths || MAX_SUBSCRIPTION_PERIOD_MONTHS)
            currentTariffId.value = String(currentTariff?.integrationId || '')
            isCurrentTariffTrial.value = Boolean(currentTariff?.isTrial)

            const baseScreen = mapResponseToScreen(
                resolvedPayload || {
                    multiSubscription: null,
                    addonSubscriptions: [],
                }
            )

            const existingSubscriptionIds = new Set<number>()
            const registerSubscriptionId = (id?: number | null) => {
                const numericId = Number(id)
                if (numericId) existingSubscriptionIds.add(numericId)
            }
            registerSubscriptionId(baseScreen.multiSubscription?.subscriptionId)
            baseScreen.addonSubscriptions.forEach(addon => registerSubscriptionId(addon.subscriptionId))

            const {
                tariffRows: pendingTariffRows,
                standaloneAddons: pendingStandaloneAddons,
                renewalPayloadBySubscriptionId,
            } = splitPendingSlots(pendingSlots, existingSubscriptionIds)

            const markPendingRenewal = <T extends UiAddonSubscription>(addon: T): T => {
                const payloadId = addon.subscriptionId
                    ? renewalPayloadBySubscriptionId.get(Number(addon.subscriptionId))
                    : undefined
                if (!payloadId) return addon
                return { ...addon, pendingPayment: true, paymentPayloadId: payloadId }
            }

            screen.value = {
                ...baseScreen,
                multiSubscription: baseScreen.multiSubscription
                    ? {
                          ...baseScreen.multiSubscription,
                          rows: [...baseScreen.multiSubscription.rows, ...pendingTariffRows],
                      }
                    : null,
                addonSubscriptions: [
                    ...baseScreen.addonSubscriptions.map(markPendingRenewal),
                    ...pendingStandaloneAddons,
                ],
            }
        }

        const handlePendingStatusRefresh = async () => {
            if (subscriptionsRefreshing.value) return

            const projectId = project.value?.id
            if (!projectId) return

            const scrollX = window.scrollX
            const scrollY = window.scrollY
            const startedAt = Date.now()

            subscriptionsRefreshing.value = true
            try {
                await Promise.all([
                    billingStore.fetchCurrentTariff(projectId),
                    load({ silent: true }),
                ])
                root?.$root?.$emit('reload-channel-slots')
                await nextTick()
                window.scrollTo(scrollX, scrollY)
            } finally {
                const remaining = 1000 - (Date.now() - startedAt)
                if (remaining > 0) {
                    await new Promise(resolve => setTimeout(resolve, remaining))
                }
                subscriptionsRefreshing.value = false
            }
        }

        const handleCancelPendingPayment = async (paymentPayloadId?: number | null) => {
            const id = Number(paymentPayloadId)
            if (!Number.isFinite(id) || id <= 0) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить платёж для отмены')
                return
            }
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (cancellingPendingPaymentId.value != null) return

            cancellingPendingPaymentId.value = id
            try {
                const { error } = await updatePaymentPayloadStatus({
                    projectId,
                    paymentPayloadId: id,
                    status: 'cancelled',
                })
                if (error) {
                    root?.$store?.dispatch('callNotify', 'Не удалось отменить платёж')
                    return
                }
                await handlePendingStatusRefresh()
            } finally {
                cancellingPendingPaymentId.value = null
            }
        }

        const handleRenewSubscription = async (
            type: 'multi' | 'addon',
            addon?: any,
            allowShorterPeriods = true,
        ) => {
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }

            if (type === 'multi' && !screen.value?.multiSubscription) {
                await openInitialTariffPurchase(projectId)
                return
            }

            // Не блокируем открытие сайдбара загрузкой тарифов.
            void billingStore.fetchTariffs(projectId)

            // Чтобы `PaymentSidebar` мог корректно посчитать solvePayment/solveRenewalSubscription,
            // нужно подставить marketplaceIntegrationId (selectedTariffId) и период.
            let current = billingStore.getCurrentTariff(projectId)
            if (!current?.integrationId) {
                await billingStore.fetchCurrentTariff(projectId)
                current = billingStore.getCurrentTariff(projectId)
            }
            // Аддон продлевается своим заказом и тарифа не требует: так продлевает план проект «только BA».
            if (!current?.integrationId && type === 'multi') {
                root?.$store?.dispatch('callNotify', 'Не удалось получить текущий тариф')
                return
            }

            if (current?.integrationId) billingStore.selectedTariffId = current.integrationId
            const multiSubscriptionWithPeriod = screen.value?.multiSubscription as
                | ({ periodFrom?: string; periodTo?: string } & NonNullable<typeof screen.value>['multiSubscription'])
                | null
            const targetPeriodRaw =
                type === 'multi'
                    ? getMonthsByPeriodRange(
                        multiSubscriptionWithPeriod?.periodFrom || '',
                        multiSubscriptionWithPeriod?.periodTo || ''
                    ) ?? ''
                    : getMonthsByPeriodRange(addon?.periodFrom || '', addon?.periodTo || '') ?? ''
            const targetPeriod =
                targetPeriodRaw === '2' || Number(targetPeriodRaw) === 2
                    ? '1'
                    : targetPeriodRaw || String(current?.periodInMonths === 2 ? 1 : current?.periodInMonths || '12')

            billingStore.selectedSubscriptionPeriod = targetPeriod

            if (type === 'addon') {
                const row = addon?.rows?.[0]
                if (!row?.channelKind) {
                    root?.$store?.dispatch('callNotify', 'Не удалось определить канал для продления')
                    return
                }

                // Режим "add-channel" в сайдбаре включаем через addChannelContext.
                billingStore.setAddChannelContext({
                    name: row.name,
                    icon: channelIconSrc(row.channelKind),
                    kind: row.channelKind,
                    itemKind: row.kind || null,
                    integrationId: row.integrationId || null,
                    subscriptionId:
                        typeof addon?.subscriptionId === 'number'
                            ? addon.subscriptionId
                            : typeof row?.subscriptionId === 'number'
                              ? row.subscriptionId
                              : null,
                    renewalMode: 'standalone',
                })
            } else {
                // Для продления "основной" подписки отключаем add-channel режим.
                billingStore.setAddChannelContext(null)
            }

            // renewal=true — сайдбар переключается в режим продления.
            billingStore.setPaymentModalOpened(true, true, allowShorterPeriods, type === 'multi')
        }

        const handleRenewFromOverview = () => handleRenewSubscription('multi', undefined, false)

        const unsubscribeSubscription = async (subscriptionId: number) => {
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                return
            }

            confirmStore.confirm({
                text: 'Вы уверены что хотите отключить подписку?',
                onConfirm: async () => {
                    const { error } = await toggleSubscription({
                        projectId,
                        subscriptionId,
                        status: 'disable',
                    })
                    if (error) {
                        root?.$store?.dispatch('callNotify', 'Не удалось отключить автопродление')
                        return
                    }
                    await load()
                    root?.$store?.dispatch('callNotify', 'Автопродление отключено')
                },
            })
        }
        const enableSubscriptionAutoRenewal = async (subscriptionId: number) => {
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку')
                return
            }
            const { error } = await toggleSubscription({
                projectId,
                subscriptionId,
                status: 'enable',
            })
            if (error) {
                root?.$store?.dispatch('callNotify', 'Не удалось включить автопродление')
                return
            }
            await load()
            root?.$store?.dispatch('callNotify', 'Автопродление включено')
        }

        const onAddonAutoRenewalMenuClick = (item: {
            addon: UiAddonSubscription
            row: SubscriptionLineRowDto
        }) => {
            const row = item.row as UiSubscriptionLineRow
            if (row?.kind === 'module' && !isChannelLikeModuleRow(row)) {
                const subscriptionId = item.addon.subscriptionId
                if (!subscriptionId) {
                    root?.$store?.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                    return
                }
                if (item.addon.isAutoRenewalEnabled) {
                    void unsubscribeSubscription(subscriptionId)
                    return
                }
                void enableSubscriptionAutoRenewal(subscriptionId)
                return
            }
            if (isChannelLikeModuleRow(row)) {
                void openModuleCancelSubscription(
                    item.addon.subscriptionId,
                    item.addon.periodTo,
                    item.addon.isAutoRenewalEnabled === true,
                    { moduleName: item.row.name },
                )
                return
            }
            void openChannelCancelSubscription(
                item.addon.subscriptionId,
                item.addon.periodTo,
                item.addon.isAutoRenewalEnabled === true,
                {
                    renewalMode: item.addon.renewalMode,
                    channelName: item.row.name,
                }
            )
        }

        const openChannelCancelSubscription = async (
            subscriptionId: number,
            periodTo?: string,
            isAutoRenewalEnabled = true,
            options?: { renewalMode?: string; channelName?: string }
        ) => {
            if (!isAutoRenewalEnabled) {
                await enableSubscriptionAutoRenewal(subscriptionId)
                return
            }
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                return
            }
            const { data, error } = await getSlots({ projectId })
            if (error || !Array.isArray(data)) {
                root?.$store?.dispatch('callNotify', 'Не удалось получить данные слотов')
                return
            }
            const slot = data.find((s: any) => Number(s?.subscriptionId) === Number(subscriptionId))
            if (!slot) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить слот для отмены подписки')
                return
            }
            const connectorId = slot?.channelConnectorId
            const slotId = slot?.id
            if (!connectorId && !slotId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить слот для отмены подписки')
                return
            }
            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            applyCancelSubscriptionQueryParams({
                renewalMode: slot?.renewalMode,
                channelName: options?.channelName,
                periodTo: periodTo || null,
            })
            root?.$store?.commit(
                'setConnectorSettingsSidebarId',
                connectorId
                    ? `cancel-subscription:${connectorId}:from-overview`
                    : `cancel-subscription:slot:${slotId}:from-overview`
            )
            root?.$store?.commit('setIsConnectorSettingsSidebar', true)
        }
        const openTariffCancelSubscription = async (
            subscriptionId: number,
            periodTo?: string,
            rows?: any[],
            isAutoRenewalEnabled = true
        ) => {
            if (!isAutoRenewalEnabled) {
                const provider = String(
                    screen.value?.multiSubscription?.paymentMethod?.provider || ''
                ).toLowerCase()
                if (provider === 'invoice') {
                    root?.$store?.dispatch(
                        'callNotify',
                        'Автопродление недоступно при оплате по счету. Чтобы включить его, оплатите подписку картой.'
                    )
                    return
                }
                await enableSubscriptionAutoRenewal(subscriptionId)
                return
            }
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                return
            }
            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            applyCancelSubscriptionQueryParams({
                renewalMode: null,
                channelName: null,
                periodTo: periodTo || null,
            })
            const nextUrl = new URL(window.location.href)
            const tariffIncludedItems = (rows || [])
                .filter((row: any) => row?.roleLabel !== 'Тариф')
                .map((row: any) => ({
                    name: String(row?.name || '').trim(),
                    inTariff: row?.roleLabel === 'Входит в тариф',
                }))
                .filter((row: any) => row.name)
            if (tariffIncludedItems.length) {
                nextUrl.searchParams.set('cancelTariffItems', JSON.stringify(tariffIncludedItems))
            } else {
                nextUrl.searchParams.delete('cancelTariffItems')
            }
            window.history.replaceState({}, '', nextUrl.toString())
            root?.$store?.commit('setConnectorSettingsSidebarId', `cancel-subscription:tariff:${subscriptionId}`)
            root?.$store?.commit('setIsConnectorSettingsSidebar', true)
        }

        const openModuleCancelSubscription = async (
            subscriptionId: number,
            periodTo?: string,
            isAutoRenewalEnabled = true,
            options?: { moduleName?: string }
        ) => {
            if (!isAutoRenewalEnabled) {
                await enableSubscriptionAutoRenewal(subscriptionId)
                return
            }
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку для отмены')
                return
            }
            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            applyCancelSubscriptionQueryParams({
                renewalMode: null,
                channelName: options?.moduleName || null,
                periodTo: periodTo || null,
            })
            const nextUrl = new URL(window.location.href)
            nextUrl.searchParams.delete('cancelTariffItems')
            window.history.replaceState({}, '', nextUrl.toString())
            root?.$store?.commit('setConnectorSettingsSidebarId', `cancel-subscription:module:${subscriptionId}`)
            root?.$store?.commit('setIsConnectorSettingsSidebar', true)
        }

        const handlePromoBlockClick = async (actionType: 'claim_discount' | 'claim_channel' | 'go_tariff') => {
            if (actionType === 'claim_discount') {
                const projectId = project.value?.id
                if (!projectId) {
                    root?.$store?.dispatch('callNotify', 'Проект не найден')
                    return
                }

                let current = billingStore.getCurrentTariff(projectId)
                if (!current?.integrationId) {
                    await billingStore.fetchCurrentTariff(projectId)
                    current = billingStore.getCurrentTariff(projectId)
                }
                if (!current?.integrationId) {
                    root?.$store?.dispatch('callNotify', 'Не удалось получить текущий тариф')
                    return
                }

                billingStore.selectedTariffId = current.integrationId
                billingStore.selectedSubscriptionPeriod = String(MAX_SUBSCRIPTION_PERIOD_MONTHS)
                billingStore.setAddChannelContext(null)
                billingStore.setPaymentModalOpened(true, true, true, true)
            } else if (actionType === 'claim_channel') {
                const projectId = project.value?.id
                if (!projectId) {
                    root?.$store?.dispatch('callNotify', 'Проект не найден')
                    return
                }

                billingStore.setAddChannelContext({
                    name: 'WA/TG/MAX/SMS',
                    icon: null,
                    kind: 'bundle',
                })
                billingStore.setPaymentModalOpened(true, false)
            } else if (actionType === 'go_tariff') {
                const projectId = project.value?.id
                if (!projectId) {
                    root?.$store?.dispatch('callNotify', 'Проект не найден')
                    return
                }

                billingStore.setAddChannelContext(null)
                billingStore.selectedTariffId = SMART_BOT_TARIFF_ID
                const targetPeriod = getUpgradeSubscriptionPeriod()
                billingStore.selectedSubscriptionPeriod = targetPeriod
                billingStore.setPaymentModalOpened(false)
                root?.$router?.push(
                    `/project/${projectId}/billing/?section=tariffs&highlighted=true&default=${SMART_BOT_TARIFF_ID}&period=${targetPeriod}`
                )
            }
        }

        const addonHasVisiblePeriod = (item: {
            addon: UiAddonSubscription
            paymentStatus: AddonPaymentStatus
        }) => {
            if (item.paymentStatus?.tone === 'processing') return false
            return Boolean(String(item.addon?.periodDescription || '').trim())
        }

        const isAddonAutoRenewalBlockVisible = (item: {
            addon: UiAddonSubscription
            paymentStatus: AddonPaymentStatus
        }) =>
            item.addon?.isAutoRenewalEnabled === true &&
            item.paymentStatus?.tone !== 'processing'

        const isAddonNameCellCenteredVertically = (item: {
            addon: UiAddonSubscription
            paymentStatus: AddonPaymentStatus
        }) => {
            const hasPeriod = addonHasVisiblePeriod(item)
            const hasStatus = Boolean(item.paymentStatus)
            if (hasPeriod && hasStatus) return false
            return true
        }

        const getAutoRenewalMenuText = (isAutoRenewalEnabled: boolean) =>
            isAutoRenewalEnabled ? 'Выключить автопродление' : 'Включить автопродление'

        const getAddonAutoRenewalMenuText = (addon: UiAddonSubscription) =>
            getAutoRenewalMenuText(addon?.isAutoRenewalEnabled === true)

        const detachChannelModalOpened = ref(false)
        const detachChannelModalChannelName = ref('')
        const detachChannelModalIsModule = ref(false)
        const detachChannelModalSubscriptionId = ref<number | null>(null)
        const detachChannelModalLoading = ref(false)
        const scheduledChangeCancelModalOpened = ref(false)
        const scheduledChangeCancelModalLoading = ref(false)
        const scheduledChangeCancelModalDate = computed(() => {
            const activeFrom = screen.value?.multiSubscription?.scheduledChanges?.activeFrom || ''
            return formatScheduledChangeDate(activeFrom)
        })
        const scheduledChangePaymentStatus = computed(() =>
            String(screen.value?.multiSubscription?.scheduledChanges?.paymentStatus || '').toLowerCase()
        )
        const isPaidOrPendingScheduledChange = computed(
            () =>
                scheduledChangePaymentStatus.value === 'pending' ||
                scheduledChangePaymentStatus.value === 'paid'
        )
        const scheduledChangeCancelModalTitle = computed(() =>
            isPaidOrPendingScheduledChange.value
                ? 'Отмена понижения тарифа'
                : 'Отменить запланированное изменение?'
        )
        const scheduledChangeCancelModalText = computed(() =>
            isPaidOrPendingScheduledChange.value
                ? 'Обратитесь в поддержку, так как Вы уже провели оплату будущего тарифа.'
                : `После ${scheduledChangeCancelModalDate.value} ничего не изменится. Подписка сохранит текущий тариф, период и цену.`
        )
        const resolveScheduledPaymentUrl = () => {
            const change = screen.value?.multiSubscription?.scheduledChanges
            if (!change) return ''
            const direct = typeof change.paymentUrl === 'string' ? change.paymentUrl.trim() : ''
            if (direct) return direct
            const payloadId = Number(change.paymentPayloadId)
            const projectId = project.value?.id
            if (!Number.isFinite(payloadId) || payloadId <= 0 || !projectId) return ''
            const origin = String(config.frontendEndpoint || window.location.origin).replace(/\/$/, '')
            return `${origin}/project/${projectId}/payment/${payloadId}`
        }
        const handleScheduledChangePay = () => {
            const paymentUrl = resolveScheduledPaymentUrl()
            if (!paymentUrl) {
                root?.$store?.dispatch('callNotify', 'Не удалось получить ссылку на оплату')
                return
            }
            window.open(paymentUrl, '_blank', 'noopener,noreferrer')
        }
        const handleScheduledChangeConfirmPaid = async () => {
            const projectId = project.value?.id
            const paymentPayloadId = Number(
                screen.value?.multiSubscription?.scheduledChanges?.paymentPayloadId
            )
            if (projectId && Number.isFinite(paymentPayloadId) && paymentPayloadId > 0) {
                await paymentConfirmClick({ projectId, paymentPayloadId })
            }
            await handlePendingStatusRefresh()
            const status = String(
                screen.value?.multiSubscription?.scheduledChanges?.paymentStatus || ''
            ).toLowerCase()
            if (status === 'created' || status === 'failed') {
                root?.$store?.dispatch('callNotify', 'Информация о платеже еще не поступила')
            }
        }

        const isMultiSubscriptionDetachableRow = (row: UiSubscriptionLineRow) =>
            !row.pendingPayment &&
            row.kind !== 'tariff' &&
            !Boolean(row.inTariff) &&
            typeof row.subscriptionId === 'number' &&
            row.subscriptionId > 0

        const onMultiSubscriptionDetachClick = (row: UiSubscriptionLineRow) => {
            detachChannelModalChannelName.value = row.name || 'канал'
            detachChannelModalIsModule.value = row.kind === 'module'
            detachChannelModalSubscriptionId.value =
                typeof row.subscriptionId === 'number' ? row.subscriptionId : null
            detachChannelModalOpened.value = true
        }

        const closeDetachChannelModal = () => {
            detachChannelModalOpened.value = false
            detachChannelModalSubscriptionId.value = null
            detachChannelModalIsModule.value = false
            detachChannelModalLoading.value = false
        }

        const handleDetachChannelCreatePackage = async () => {
            const subscriptionId = detachChannelModalSubscriptionId.value
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить канал для отвязки')
                return
            }
            detachChannelModalLoading.value = true
            const { error } = await unchainSlot({ subscriptionId })
            if (error) {
                detachChannelModalLoading.value = false
                root?.$store?.dispatch('callNotify', 'Ошибка при отвязке дополнительного канала')
                return
            }
            detachChannelModalLoading.value = false
            closeDetachChannelModal()
            await load()
            root?.$store?.dispatch('callNotify', 'Канал переведен в отдельный пакет')
        }

        const openScheduledChangeCancelModal = () => {
            scheduledChangeCancelModalOpened.value = true
        }

        const closeScheduledChangeCancelModal = () => {
            if (scheduledChangeCancelModalLoading.value) return
            scheduledChangeCancelModalOpened.value = false
        }

        const handleScheduledChangeCancelModalInput = (value: boolean) => {
            if (!value) closeScheduledChangeCancelModal()
        }

        const cancelScheduledTariffChange = async () => {
            const projectId = project.value?.id
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            scheduledChangeCancelModalLoading.value = true
            const { error } = await cancelScheduleTariffChange(projectId)
            scheduledChangeCancelModalLoading.value = false
            if (error) {
                root?.$store?.dispatch('callNotify', 'Не удалось отменить изменения тарифа')
                return
            }
            scheduledChangeCancelModalOpened.value = false
            await load()
            root?.$store?.dispatch('callNotify', 'Изменения тарифа отменены')
        }

        const reloadSubscriptionsInPlace = async () => {
            const projectId = project.value?.id
            if (projectId) {
                try {
                    await root?.$store?.dispatch('project/fetchProject', projectId)
                } catch (e) {
                    // слоты всё равно перезагружаем — статус канала обновится, когда появятся коннекторы
                }
            }
            await load({ silent: Boolean(screen.value) })
        }

        onMounted(() => {
            parsePaymentHighlightFromRoute()
            load()
            root?.$root?.$on('reload-subscriptions', reloadSubscriptionsInPlace)
            root?.$root?.$on('reload-channel-slots', reloadSubscriptionsInPlace)
        })
        onUnmounted(() => {
            root?.$root?.$off('reload-subscriptions', reloadSubscriptionsInPlace)
            root?.$root?.$off('reload-channel-slots', reloadSubscriptionsInPlace)
        })
        watch(
            () => project.value?.id,
            () => {
                load()
            }
        )
        watch(
            () => [root?.$route?.query?.status, root?.$route?.query?.id],
            () => {
                paymentHighlightScrollScheduled.value = false
                paymentHighlightRowRef.value = null
                parsePaymentHighlightFromRoute()
            }
        )
        watch(
            () => [loading.value, paymentHighlightTargetId.value, screen.value],
            async ([isLoading]) => {
                if (isLoading || !paymentHighlightTargetId.value || paymentHighlightScrollScheduled.value) return
                await scrollToPaymentHighlight()
            }
        )

        return {
            project,
            loading,
            loadError,
            screen,
            legacyLayoutEnabled,
            currentPeriodInMonths,
            canUpgradeSubscription,
            subscriptionsRefreshing,
            cancellingPendingPaymentId,
            handlePendingStatusRefresh,
            handleCancelPendingPayment,
            multiSubscriptionTableRows,
            addonSubscriptionsTableRows,
            channelSlots,
            formatRub,
            formatMonthly,
            channelClass,
            channelIconSrc,
            formatScheduledChangeDate,
            formatScheduledChangePeriod,
            handleRenewSubscription,
            handleRenewFromOverview,
            handleUpgradeSubscription,
            unsubscribeSubscription,
            onAddonAutoRenewalMenuClick,
            openChannelCancelSubscription,
            openTariffCancelSubscription,
            getPaymentDeadlineStatus,
            addonHasVisiblePeriod,
            isAddonAutoRenewalBlockVisible,
            isAddonNameCellCenteredVertically,
            formatAutoRenewalNextPaymentLine,
            handlePromoBlockClick,
            detachChannelModalOpened,
            detachChannelModalChannelName,
            detachChannelModalIsModule,
            detachChannelModalLoading,
            scheduledChangeCancelModalOpened,
            scheduledChangeCancelModalLoading,
            scheduledChangeCancelModalDate,
            scheduledChangeCancelModalTitle,
            scheduledChangeCancelModalText,
            isPaidOrPendingScheduledChange,
            openScheduledChangeCancelModal,
            closeScheduledChangeCancelModal,
            handleScheduledChangeCancelModalInput,
            cancelScheduledTariffChange,
            handleScheduledChangePay,
            handleScheduledChangeConfirmPaid,
            closeDetachChannelModal,
            handleDetachChannelCreatePackage,
            isMultiSubscriptionDetachableRow,
            onMultiSubscriptionDetachClick,
            isUnoccupiedSlotRow,
            isMaxBusinessSlotRow,
            openSlotConnect,
            isPaymentHighlighted,
            setPaymentHighlightRowRef,
            getAutoRenewalMenuText,
            getAddonAutoRenewalMenuText,
        }
    },
})
